
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRightLeft, X, GripVertical, ListFilter, Bike, Truck, MessageSquare, AlertCircle, CheckCircle2, ChevronRight, Maximize2, Minimize2, ChevronUp, ChevronDown } from 'lucide-react';
import { Order } from '../../types';
import { AuditTooltip } from '../shared/AuditTooltip';
import { toast } from 'react-hot-toast';

interface Props {
    isAdjusting: boolean;
    setIsAdjusting: (v: boolean) => void;
    localFleet: Order[];
    localApps: Order[];
    localTriage: Order[];
    setLocalFleet: React.Dispatch<React.SetStateAction<Order[]>>;
    setLocalApps: React.Dispatch<React.SetStateAction<Order[]>>;
    setLocalTriage: React.Dispatch<React.SetStateAction<Order[]>>;
    onConfirmAdjustment: (payload: { reason: string; ownRouteGroups: Record<string, string>; thirdRouteGroups: Record<string, string>; ownActiveRouteByOrder?: Record<string, string> }) => void;
    activeDriverRoutes?: { driverId: string; driverName: string; activeOrders: number; isInProgress: boolean }[];
    isGlassMode?: boolean; // vGlass: Controle de estilo
}

const PRESET_REASONS = [
    "Motoboy de confiança",
    "Cliente VIP / Prioritário",
    "Região de difícil acesso",
    "Agrupamento tático melhor",
    "Pedido com erro no mapa"
];

const parseRouteIndex = (value?: string) => {
    if (!value) return 1;
    const match = value.match(/route_(\d+)/);
    if (!match) return 1;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

export const ManualIntervention: React.FC<Props> = ({
    isAdjusting, setIsAdjusting, localFleet, localApps, localTriage, setLocalFleet, setLocalApps, setLocalTriage, onConfirmAdjustment, isGlassMode = false,
    activeDriverRoutes = []
}) => {
    const [isJustifying, setIsJustifying] = useState(false);
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [customReason, setCustomReason] = useState<string>('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [fleetRouteCount, setFleetRouteCount] = useState(1);
    const [appsRouteCount, setAppsRouteCount] = useState(1);
    const [ownActiveRouteByOrder, setOwnActiveRouteByOrder] = useState<Record<string, string>>({});

    const getRouteLabel = (value?: string) => `Rota ${parseRouteIndex(value)}`;

    useEffect(() => {
        if (!isAdjusting) return;
        const maxFleet = Math.max(1, ...localFleet.map(o => parseRouteIndex(o.manualRouteGroup)));
        const maxApps = Math.max(1, ...localApps.map(o => parseRouteIndex(o.manualRouteGroup)));
        setFleetRouteCount(maxFleet);
        setAppsRouteCount(maxApps);
    }, [isAdjusting, localFleet, localApps]);

    useEffect(() => {
        if (!isAdjusting) {
            setOwnActiveRouteByOrder({});
        }
    }, [isAdjusting]);

    const wordCount = customReason.trim() === "" ? 0 : customReason.trim().split(/\s+/).length;
    const isCustomValid = wordCount >= 4;
    const canFinalize = selectedReason !== "" || isCustomValid;

    const handleDragStart = (e: React.DragEvent, orderId: string, source: 'fleet' | 'apps' | 'triage') => {
        e.dataTransfer.setData('orderId', orderId);
        e.dataTransfer.setData('source', source);
        e.dataTransfer.effectAllowed = 'move';
    };

    // v59.1: Preserva estrutura de rota ao arrastar entre colunas
    // Extrai o índice da rota existente e aplica ao novo tipo
    const handleDrop = (e: React.DragEvent, target: 'fleet' | 'apps' | 'triage') => {
        e.preventDefault();
        const orderId = e.dataTransfer.getData('orderId');
        const source = e.dataTransfer.getData('source') as 'fleet' | 'apps' | 'triage';

        if (source === target) return;

        let orderToMove: Order | undefined;

        if (source === 'fleet') {
            orderToMove = localFleet.find(o => o.id === orderId);
            if (orderToMove) setLocalFleet(prev => prev.filter(o => o.id !== orderId));
            setOwnActiveRouteByOrder(prev => {
                if (!prev[orderId]) return prev;
                const next = { ...prev };
                delete next[orderId];
                return next;
            });
        } else if (source === 'apps') {
            orderToMove = localApps.find(o => o.id === orderId);
            if (orderToMove) setLocalApps(prev => prev.filter(o => o.id !== orderId));
        } else {
            orderToMove = localTriage.find(o => o.id === orderId);
            if (orderToMove) setLocalTriage(prev => prev.filter(o => o.id !== orderId));
        }

        if (!orderToMove) return;

        // Preserva o índice da rota ao mover entre colunas
        const existingRouteIndex = parseRouteIndex(orderToMove.manualRouteGroup);

        if (target === 'fleet') {
            setLocalFleet(prev => [...prev, { ...orderToMove!, manualRouteGroup: `own_route_${existingRouteIndex}` }]);
        }
        else if (target === 'apps') {
            setLocalApps(prev => [...prev, { ...orderToMove!, manualRouteGroup: `third_route_${existingRouteIndex}` }]);
        }
        else setLocalTriage(prev => [...prev, orderToMove!]);
    };

    const assignOrderToRoute = (orderId: string, list: 'fleet' | 'apps', routeIndex: number) => {
        const routeGroup = list === 'fleet' ? `own_route_${routeIndex}` : `third_route_${routeIndex}`;
        const setter = list === 'fleet' ? setLocalFleet : setLocalApps;
        setter(prev => prev.map(order => order.id === orderId ? { ...order, manualRouteGroup: routeGroup } : order));
    };

    const assignOrderToActiveDriverRoute = (orderId: string, driverId: string) => {
        setOwnActiveRouteByOrder(prev => {
            if (!driverId) {
                if (!prev[orderId]) return prev;
                const next = { ...prev };
                delete next[orderId];
                return next;
            }
            return { ...prev, [orderId]: driverId };
        });
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    // v57.7: Funções de reordenação dentro da mesma coluna
    const moveOrderUp = (orderId: string, list: 'fleet' | 'apps') => {
        const setter = list === 'fleet' ? setLocalFleet : setLocalApps;

        setter(prev => {
            const index = prev.findIndex(o => o.id === orderId);
            if (index <= 0) return prev; // Já é o primeiro

            const newArray = [...prev];
            [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
            return newArray;
        });
    };

    const moveOrderDown = (orderId: string, list: 'fleet' | 'apps') => {
        const setter = list === 'fleet' ? setLocalFleet : setLocalApps;

        setter(prev => {
            const index = prev.findIndex(o => o.id === orderId);
            if (index < 0 || index >= prev.length - 1) return prev; // Já é o último

            const newArray = [...prev];
            [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
            return newArray;
        });
    };

    const handleAttemptFinalize = () => {
        const finalReason = selectedReason || customReason;
        const ownRouteGroups = localFleet.reduce<Record<string, string>>((acc, order) => {
            acc[order.id] = order.manualRouteGroup || 'own_route_1';
            return acc;
        }, {});
        const thirdRouteGroups = localApps.reduce<Record<string, string>>((acc, order) => {
            acc[order.id] = order.manualRouteGroup || 'third_route_1';
            return acc;
        }, {});
        const selectedOwnActiveRoutes = localFleet.reduce<Record<string, string>>((acc, order) => {
            const selectedDriverId = ownActiveRouteByOrder[order.id];
            if (selectedDriverId) acc[order.id] = selectedDriverId;
            return acc;
        }, {});

        onConfirmAdjustment({
            reason: finalReason,
            ownRouteGroups,
            thirdRouteGroups,
            ownActiveRouteByOrder: selectedOwnActiveRoutes
        });
        setIsJustifying(false);
        setSelectedReason('');
        setCustomReason('');
        setOwnActiveRouteByOrder({});
    };

    const content = (
        <AnimatePresence>
            {isAdjusting && (
                <div
                    className={`${isExpanded ? 'fixed top-0 bottom-0 right-0 left-0 md:left-[420px] z-[1000] border-l border-border-dim' : 'absolute inset-0 z-50'} p-4 md:p-6 flex flex-col overflow-hidden transition-all duration-300`}
                    style={isGlassMode ? {
                        backgroundColor: 'rgba(var(--glass-panel-bg))',
                        backdropFilter: 'blur(32px)',
                        WebkitBackdropFilter: 'blur(32px)'
                    } : {
                        backgroundColor: 'rgba(var(--bg-main), 0.98)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)'
                    }}
                >
                    <div className="flex justify-between items-center mb-6 shrink-0">
                        <h2 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                            <div className="p-2 bg-sal-cyber-teal/10 rounded-lg">
                                <ArrowRightLeft className="w-6 h-6 text-sal-cyber-teal" />
                            </div>
                            {isJustifying ? "Justificativa Obrigatória" : "Ajuste Fino de Distribuição"}
                        </h2>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setIsExpanded(!isExpanded)} className={`p-2 rounded-full transition-colors ${isExpanded ? 'bg-sal-cyber-teal text-sal-gunmetal' : 'hover:bg-app-bg text-icon-muted hover:text-text-primary'}`} title={isExpanded ? "Restaurar" : "Expandir"}>
                                {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                            </button>
                            <button onClick={() => { setIsAdjusting(false); setIsJustifying(false); }} className="p-2 hover:bg-app-bg rounded-full text-icon-muted hover:text-text-primary transition-colors"><X className="w-6 h-6" /></button>
                        </div>
                    </div>

                    {!isJustifying ? (
                        <div className="flex-1 flex flex-col md:flex-row gap-4 overflow-hidden">
                            <div className="flex-1 bg-card-bg/30 border-2 border-dashed border-border-dim rounded-[2rem] flex flex-col overflow-hidden" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, 'triage')}>
                                <div className="p-4 bg-card-bg/50 text-text-muted font-black uppercase text-xs tracking-widest flex items-center justify-center gap-2">
                                    <ListFilter className="w-3.5 h-3.5" /> Fila de Triagem ({localTriage.length})
                                </div>
                                <div className="flex-1 overflow-y-auto p-3 space-y-2 no-scrollbar">
                                    {localTriage.map(order => (
                                        <div key={order.id} draggable onDragStart={(e) => handleDragStart(e, order.id, 'triage')} className="bg-card-bg p-3 rounded-2xl border border-border-dim shadow-sm cursor-grab active:cursor-grabbing hover:border-sal-cyber-teal/40 transition-all group">
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="text-xs font-bold text-text-secondary">#{order.id} <span className="text-text-muted font-medium ml-1 truncate">{order.customerName.split(' ')[0]}</span></span>
                                                <GripVertical className="w-3.5 h-3.5 text-icon-default group-hover:text-text-primary" />
                                            </div>
                                            <div className="flex justify-between items-end">
                                                <span className="text-xs text-slate-600 uppercase font-black">{order.neighborhood}</span>
                                                <span className="text-xs font-mono text-slate-500 font-bold">R$ {order.orderValue.toFixed(0)}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {localTriage.length === 0 && <div className="h-full flex flex-col items-center justify-center opacity-20 text-slate-500"><ListFilter className="w-8 h-8 mb-2" /><span className="text-xs font-black uppercase">Tudo Roteirizado</span></div>}
                                </div>
                            </div>

                            <div className="flex-1 bg-sal-solid-indigo/5 border-2 border-dashed border-sal-solid-indigo rounded-[2rem] flex flex-col overflow-hidden" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, 'fleet')}>
                                <div className="p-4 bg-sal-solid-indigo/10 text-sal-solid-indigo font-black uppercase text-xs tracking-widest flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2"><Bike className="w-3.5 h-3.5" /> Frota da Casa ({localFleet.length})</div>
                                    <button type="button" onClick={() => setFleetRouteCount(prev => prev + 1)} className="px-2 py-1 rounded-md border border-sal-solid-indigo/40 text-xs font-black hover:bg-sal-solid-indigo/20 transition-colors">
                                        + Rota
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-3 space-y-2 no-scrollbar">
                                    {localFleet.map((order, idx) => (
                                        <div key={order.id} draggable onDragStart={(e) => handleDragStart(e, order.id, 'fleet')} className="bg-card-bg p-3 rounded-2xl border border-sal-solid-indigo/20 shadow-sm cursor-grab active:cursor-grabbing hover:border-sal-solid-indigo/50 transition-all group">
                                            <div className="flex justify-between items-start mb-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-5 h-5 rounded-full bg-sal-solid-indigo text-white text-xs font-black flex items-center justify-center shrink-0">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="text-xs font-bold text-text-primary">#{order.id} <span className="text-text-muted font-medium ml-1 truncate">{order.customerName.split(' ')[0]}</span></span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <button
                                                        onClick={() => moveOrderUp(order.id, 'fleet')}
                                                        disabled={idx === 0}
                                                        className="p-0.5 rounded hover:bg-sal-solid-indigo/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        title="Mover para cima (entrega antes)"
                                                    >
                                                        <ChevronUp className="w-3.5 h-3.5 text-sal-solid-indigo" />
                                                    </button>
                                                    <button
                                                        onClick={() => moveOrderDown(order.id, 'fleet')}
                                                        disabled={idx === localFleet.length - 1}
                                                        className="p-0.5 rounded hover:bg-sal-solid-indigo/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        title="Mover para baixo (entrega depois)"
                                                    >
                                                        <ChevronDown className="w-3.5 h-3.5 text-sal-solid-indigo" />
                                                    </button>
                                                    <GripVertical className="w-3.5 h-3.5 text-icon-default group-hover:text-text-primary" />
                                                </div>
                                            </div>
                                            <div className="mt-2 flex items-center gap-2">
                                                <span className="text-xs text-sal-solid-indigo uppercase font-black">{order.neighborhood}</span>
                                                <span className="text-xs px-1.5 py-0.5 rounded bg-sal-solid-indigo/15 text-sal-solid-indigo font-black uppercase">{getRouteLabel(order.manualRouteGroup)}</span>
                                                <select
                                                    value={parseRouteIndex(order.manualRouteGroup)}
                                                    onChange={(e) => assignOrderToRoute(order.id, 'fleet', Number(e.target.value))}
                                                    className="ml-auto bg-black/10 border border-border-dim rounded-md px-2 py-1 text-xs font-bold text-text-primary"
                                                >
                                                    {Array.from({ length: fleetRouteCount }, (_, i) => i + 1).map(routeIdx => (
                                                        <option key={`own_route_${routeIdx}`} value={routeIdx}>
                                                            Rota {routeIdx}
                                                        </option>
                                                    ))}
                                                </select>
                                                {activeDriverRoutes.length > 0 && (
                                                    <select
                                                        value={ownActiveRouteByOrder[order.id] || ''}
                                                        onChange={(e) => assignOrderToActiveDriverRoute(order.id, e.target.value)}
                                                        className="bg-black/10 border border-border-dim rounded-md px-2 py-1 text-xs font-bold text-text-primary"
                                                        title="Anexar pedido a uma rota ativa de motoboy"
                                                    >
                                                        <option value="">Sem rota ativa</option>
                                                        {activeDriverRoutes.map((route) => (
                                                            <option key={`active_route_${route.driverId}`} value={route.driverId}>
                                                                {route.driverName} ({route.activeOrders}){route.isInProgress ? ' em rota' : ' no hub'}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                <AuditTooltip text="Custo previsto com motoboy da casa."><span className="text-xs font-mono text-text-muted cursor-help">R$ {order.calculatedOwnFleetCost?.toFixed(2)}</span></AuditTooltip>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex-1 bg-sal-burnt-orange/5 border-2 border-dashed border-sal-burnt-orange rounded-[2rem] flex flex-col overflow-hidden" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, 'apps')}>
                                <div className="p-4 bg-sal-burnt-orange/10 text-sal-burnt-orange font-black uppercase text-xs tracking-widest flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> Apps Parceiros ({localApps.length})</div>
                                    <button type="button" onClick={() => setAppsRouteCount(prev => prev + 1)} className="px-2 py-1 rounded-md border border-sal-burnt-orange/40 text-xs font-black hover:bg-sal-burnt-orange/20 transition-colors">
                                        + Rota
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-3 space-y-2 no-scrollbar">
                                    {localApps.map((order, idx) => (
                                        <div key={order.id} draggable onDragStart={(e) => handleDragStart(e, order.id, 'apps')} className="bg-card-bg p-3 rounded-2xl border border-sal-burnt-orange/20 shadow-sm cursor-grab active:cursor-grabbing hover:border-sal-burnt-orange/50 transition-all group">
                                            <div className="flex justify-between items-start mb-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-5 h-5 rounded-full bg-sal-burnt-orange text-white text-xs font-black flex items-center justify-center shrink-0">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="text-xs font-bold text-text-primary">#{order.id} <span className="text-text-muted font-medium ml-1 truncate">{order.customerName.split(' ')[0]}</span></span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <button
                                                        onClick={() => moveOrderUp(order.id, 'apps')}
                                                        disabled={idx === 0}
                                                        className="p-0.5 rounded hover:bg-sal-burnt-orange/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        title="Mover para cima (entrega antes)"
                                                    >
                                                        <ChevronUp className="w-3.5 h-3.5 text-sal-burnt-orange" />
                                                    </button>
                                                    <button
                                                        onClick={() => moveOrderDown(order.id, 'apps')}
                                                        disabled={idx === localApps.length - 1}
                                                        className="p-0.5 rounded hover:bg-sal-burnt-orange/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        title="Mover para baixo (entrega depois)"
                                                    >
                                                        <ChevronDown className="w-3.5 h-3.5 text-sal-burnt-orange" />
                                                    </button>
                                                    <GripVertical className="w-3.5 h-3.5 text-icon-default group-hover:text-text-primary" />
                                                </div>
                                            </div>
                                            <div className="mt-2 flex items-center gap-2">
                                                <span className="text-xs text-sal-burnt-orange uppercase font-black">{order.neighborhood}</span>
                                                <span className="text-xs px-1.5 py-0.5 rounded bg-sal-burnt-orange/15 text-sal-burnt-orange font-black uppercase">{getRouteLabel(order.manualRouteGroup)}</span>
                                                <select
                                                    value={parseRouteIndex(order.manualRouteGroup)}
                                                    onChange={(e) => assignOrderToRoute(order.id, 'apps', Number(e.target.value))}
                                                    className="ml-auto bg-black/10 border border-border-dim rounded-md px-2 py-1 text-xs font-bold text-text-primary"
                                                >
                                                    {Array.from({ length: appsRouteCount }, (_, i) => i + 1).map(routeIdx => (
                                                        <option key={`third_route_${routeIdx}`} value={routeIdx}>
                                                            Rota {routeIdx}
                                                        </option>
                                                    ))}
                                                </select>
                                                <AuditTooltip text="Custo da cotação individual externa."><span className="text-xs font-mono text-sal-burnt-orange/80 cursor-help">R$ {order.costThirdParty.toFixed(2)}</span></AuditTooltip>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex-1 flex flex-col items-center justify-start md:justify-center max-w-2xl mx-auto w-full space-y-4 md:space-y-8 overflow-y-auto custom-scrollbar p-2"
                        >
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-amber-500/10 rounded-full flex items-center justify-center border border-amber-500/20 text-amber-500 animate-pulse shrink-0">
                                <MessageSquare className="w-8 h-8 md:w-10 md:h-10" />
                            </div>

                            <div className="text-center space-y-2">
                                <p className="text-base text-slate-300 font-medium">Por que você está alterando a recomendação da Sofia?</p>
                                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Esta informação é obrigatória para fins de auditoria.</p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                                {PRESET_REASONS.map(reason => (
                                    <button
                                        key={reason}
                                        onClick={() => { setSelectedReason(reason); setCustomReason(''); }}
                                        className={`p-4 rounded-2xl border text-xs font-black uppercase tracking-wider text-left transition-all ${selectedReason === reason ? 'bg-sal-cyber-teal border-sal-cyber-teal text-sal-gunmetal shadow-lg' : 'bg-black/5 hover:bg-black/10 border-border-dim text-text-muted hover:border-text-secondary hover:text-text-primary'}`}
                                    >
                                        {reason}
                                    </button>
                                ))}
                            </div>

                            <div className="w-full space-y-3">
                                <div className="flex justify-between items-end px-1">
                                    <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Outro Motivo / Observação</label>
                                    <span className={`text-xs font-black uppercase ${isCustomValid ? 'text-emerald-400' : 'text-slate-600'}`}>{wordCount} / 4 palavras</span>
                                </div>
                                <textarea
                                    value={customReason}
                                    onChange={(e) => { setCustomReason(e.target.value); setSelectedReason(''); }}
                                    className={`w-full bg-input-bg border rounded-2xl p-4 text-base text-text-primary placeholder-text-muted focus:outline-none transition-all h-24 ${customReason !== '' && !isCustomValid ? 'border-status-critical/50' : 'border-border-dim focus:border-sal-cyber-teal'}`}
                                    placeholder="Ex: O motoboy Wesley solicitou essa rota pois já conhece o porteiro do prédio..."
                                />
                                {customReason !== '' && !isCustomValid && (
                                    <p className="text-xs text-rose-400 font-bold uppercase flex items-center gap-1 ml-1"><AlertCircle className="w-3 h-3" /> Por favor, detalhe melhor o motivo (mínimo 4 palavras).</p>
                                )}
                            </div>
                        </motion.div>
                    )}

                    <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0 border-t border-white/5 pt-6">
                        <div className="flex items-center gap-3">
                            <button onClick={() => { if (isJustifying) setIsJustifying(false); else setIsAdjusting(false); }} className="px-8 py-4 font-black text-slate-500 hover:text-white transition-colors uppercase text-xs tracking-widest">
                                {isJustifying ? 'Voltar para o Ajuste' : 'Cancelar'}
                            </button>
                        </div>
                        <div className="flex gap-3 w-full sm:w-auto">
                            {!isJustifying ? (
                                <button
                                    onClick={() => setIsJustifying(true)}
                                    className="flex-1 sm:flex-none px-12 py-4 bg-sal-cyber-teal text-sal-gunmetal font-black rounded-2xl shadow-xl shadow-cyan-900/20 hover:brightness-110 active:scale-[0.98] transition-all uppercase text-xs tracking-widest flex items-center gap-2"
                                >
                                    Confirmar Distribuição <ChevronRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    disabled={!canFinalize}
                                    onClick={handleAttemptFinalize}
                                    className={`flex-1 sm:flex-none px-16 py-4 font-black rounded-2xl shadow-xl transition-all uppercase text-xs tracking-widest flex items-center gap-2 ${canFinalize ? 'bg-emerald-500 text-sal-gunmetal shadow-emerald-900/20 hover:brightness-110 active:scale-[0.98]' : 'bg-slate-800 text-slate-600 cursor-not-allowed opacity-50'}`}
                                >
                                    <CheckCircle2 className="w-5 h-5" /> Salvar e Aplicar Ajuste
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )
            }
        </AnimatePresence >
    );

    if (isExpanded && typeof document !== 'undefined') {
        return createPortal(content, document.body);
    }

    return content;
};
